import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import BaseFormContainer from '../../../components/Form/FormContainer'
import Calculation, { CalculationProps } from './Calculation'
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    makeStyles,
    mergeClasses,
    shorthands,
    Spinner,
    tokens,
} from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import GrantPanel from './GrantPanel'
import BaseDataPanel from './BaseDataPanel'
import { LegalAidCertificateDto, SaveCertificate } from './LegalAidCertificateForm'
import { FieldProps } from '@fluentui/react-field'
import { LegalAidCertificateProps } from '../../../pages/File/Registration/LegalAidCertificateOverview'
import Invoices from './Invoices'

import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import NavigationFormButtons from '../NavigationFormButtons'
import useVatTypes from '../../../hooks/useVatTypes'

export type LegalAidCertificateFormProps = LegalAidCertificateProps & {
    saveCertificate: SaveCertificate
}

export type LegalAidCertificatePanelProps = LegalAidCertificateProps & {
    orientation: FieldProps['orientation']
    styles: Record<'container' | 'input' | 'field' | 'formRow', string>
}

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateRows: 'auto min-content',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    accordionPanel: shorthands.margin(0),
    accordionHeader: {
        '> button': shorthands.padding(0),
    },
    sidePanel: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalL),
        ...shorthands.margin(0, '-8px'),
    },
    sidePanelLarge: shorthands.margin(0, '-16px'),
    formRow: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    input: { minWidth: 0, width: '100%', boxSizing: 'border-box', '> input': { textAlign: 'right', width: '100%' } },
    field: {
        alignItems: 'center',
        '> label': {
            ...shorthands.overflow('hidden'),
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
    },
})

export default function FormContainer(props: LegalAidCertificateFormProps): ReactElement {
    const styles = useStyles()
    const { dirty, isSubmitting, values } = useFormikContext<LegalAidCertificateDto>()
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(
        () => (isViewport('>=', Viewport.MainMenu) && isViewport('!=', Viewport.OutlookNewWide) ? 'horizontal' : 'vertical'),
        [viewport],
    )
    const { getVatType } = useVatTypes()

    const calculationProps = useMemo<CalculationProps>(() => {
        const priceComponents = [values.baseFee, values.travelFee, values.travelExpenses, values.fixedCompensation]
        const vatType = getVatType(settings.vatType ?? props.file.data.vatType)

        const subtotal = priceComponents.reduce((accumulator, currentValue) => accumulator + Number(currentValue), 0)
        const vat = subtotal * (vatType.percentage / 100)
        const total = subtotal + vat
        const compensation = total - Number(values.contribution)

        return {
            subtotal,
            vatPercentage: vatType.percentage,
            vat,
            total,
            compensation,
        }
    }, [values.baseFee, values.travelFee, values.travelExpenses, values.fixedCompensation])

    useEffect(() => {
        // Keep track if the form is dirty, so we can warn the user if they try to navigate out.
        props.setSelected({ ...props.selected, isDirty: dirty })
    }, [dirty])

    if (isSubmitting) {
        return <Spinner label={'Bezig met opslaan...'} labelPosition={'below'} />
    }

    return (
        <BaseFormContainer
            sidePanel={
                <div className={mergeClasses(styles.sidePanel, isViewport('>=', Viewport.MainMenu) && styles.sidePanelLarge)}>
                    <Invoices {...props} calculation={calculationProps} />
                    <Calculation {...calculationProps} />
                </div>
            }
        >
            <div className={styles.container}>
                <Accordion multiple collapsible defaultOpenItems={['base', 'calculation']}>
                    <AccordionItem value="base">
                        <AccordionHeader className={styles.accordionHeader}>Stamgegevens</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <BaseDataPanel orientation={orientation} styles={styles} {...props} />
                        </AccordionPanel>
                    </AccordionItem>
                    <AccordionItem value="calculation">
                        <AccordionHeader className={styles.accordionHeader}>Toekenning</AccordionHeader>
                        <AccordionPanel className={styles.accordionPanel}>
                            <GrantPanel orientation={orientation} styles={styles} {...props} />
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
                <NavigationFormButtons
                    {...props}
                    disabled={!!props.invoicePending}
                    submitDisabled={!!values.settlementInvoiceId}
                    deleteUrl={() => `/registration/legal-aid-certificate/${props.selected.id}`}
                />
            </div>
        </BaseFormContainer>
    )
}
